import { useRef, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Lock } from 'lucide-react';
import { useResponsive } from '../hooks/useResponsive';
import { bonndleAuthApi } from '../services/bonndleAuthApi';

export default function CreatePinPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDesktop } = useResponsive();
  const inputRefs = useRef([]);
  const confirmInputRefs = useRef([]);

  // Check signup flow type
  const socialSignup = location.state?.socialSignup;
  const phoneSignup = location.state?.phoneSignup;
  const otp = location.state?.otp;
  const firebaseToken = location.state?.firebaseToken; // Firebase ID token (phoneVerificationToken)
  const otpId = location.state?.otpId; // Firebase verificationId or Termii pinId

  const [step, setStep] = useState('create'); // 'create' or 'confirm'
  const [pinValue, setPinValue] = useState(['', '', '', '']);
  const [confirmPinValue, setConfirmPinValue] = useState(['', '', '', '']);
  const [error, setError] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    if (step === 'create') {
      inputRefs.current[0]?.focus();
    } else {
      confirmInputRefs.current[0]?.focus();
    }
  }, [step]);

  // Auto-advance to confirm step when all 4 digits are entered
  useEffect(() => {
    if (step === 'create' && pinValue.every((digit) => digit !== '')) {
      // Small delay for better UX - let user see the last digit before transitioning
      const timer = setTimeout(() => {
        setStep('confirm');
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [pinValue, step]);

  const handleChange = (index, value, isConfirm = false) => {
    if (!/^\d*$/.test(value)) return;
    setError('');

    const setter = isConfirm ? setConfirmPinValue : setPinValue;
    const currentValue = isConfirm ? confirmPinValue : pinValue;
    const refs = isConfirm ? confirmInputRefs : inputRefs;

    const newPin = [...currentValue];
    newPin[index] = value.slice(-1);
    setter(newPin);

    // Auto-advance to next input
    if (value && index < 3) {
      refs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e, isConfirm = false) => {
    const currentValue = isConfirm ? confirmPinValue : pinValue;
    const refs = isConfirm ? confirmInputRefs : inputRefs;

    if (e.key === 'Backspace' && !currentValue[index] && index > 0) {
      refs.current[index - 1]?.focus();
    }
  };

  const isPinValid = pinValue.every((digit) => digit !== '');
  const isConfirmValid = confirmPinValue.every((digit) => digit !== '');

  const handleCreatePin = (e) => {
    e.preventDefault();
    if (isPinValid) {
      setStep('confirm');
    }
  };

  const handleConfirmPin = async (e) => {
    e.preventDefault();
    if (!isConfirmValid) return;

    const pin = pinValue.join('');
    const confirmPin = confirmPinValue.join('');

    if (pin !== confirmPin) {
      setError('PINs do not match. Please try again.');
      setConfirmPinValue(['', '', '', '']);
      confirmInputRefs.current[0]?.focus();
      return;
    }

    setIsCreating(true);
    setError('');

    try {
      if (phoneSignup) {
        // For phone signup, pass PIN and verification tokens to tag creation page
        // Registration will happen after tag is created
        navigate('/create-tag', {
          state: {
            phoneSignup: true,
            pin,
            otp, // OTP code for backend verification
            firebaseToken, // Firebase ID token (phoneVerificationToken)
            otpId, // Firebase verificationId or Termii pinId
          }
        });
        return;
      } else if (socialSignup && otp) {
        // For social signup, create PIN with OTP verification
        await bonndleAuthApi.createSocialPin(pin, otp);
      } else {
        // For regular signup, create PIN
        await bonndleAuthApi.createPin(pin);
      }

      // PIN created successfully, go to tag creation
      navigate('/create-tag', {
        state: { socialSignup, phoneSignup }
      });
    } catch (err) {
      console.error('Failed to create PIN:', err);
      setError(err.message || 'Failed to create PIN. Please try again.');
      setConfirmPinValue(['', '', '', '']);
      confirmInputRefs.current[0]?.focus();
    } finally {
      setIsCreating(false);
    }
  };

  const handleBack = () => {
    if (step === 'confirm') {
      setStep('create');
      setConfirmPinValue(['', '', '', '']);
      setError('');
    } else {
      navigate('/verify-otp');
    }
  };

  return (
    <div className="min-h-screen min-h-dvh flex flex-col bg-white">
      <div className={`flex-1 flex flex-col ${isDesktop ? 'max-w-md mx-auto w-full' : ''}`}>
        {/* Header */}
        <header className="sticky top-0 bg-white z-10 flex items-center gap-4 px-4 pb-3 pt-[calc(0.75rem+env(safe-area-inset-top))] border-b border-gray-100">
          <button
            onClick={handleBack}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-full"
          >
            <ArrowLeft size={24} />
          </button>
          <h1 className="text-xl font-bold">Create An Account</h1>
        </header>

        {/* Content */}
        {step === 'create' ? (
          <form onSubmit={handleCreatePin} className="flex-1 px-6 py-8 flex flex-col">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock className="text-indigo-600" size={28} />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Create your PIN</h2>
              <p className="text-gray-600">
                Create a 4-digit PIN to secure your account. You'll use this to sign in.
              </p>
            </div>

            {/* PIN Input */}
            <div className="flex justify-center gap-4 mb-8">
              {pinValue.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="password"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleChange(index, e.target.value, false)}
                  onKeyDown={(e) => handleKeyDown(index, e, false)}
                  className="w-14 h-14 text-center text-2xl font-bold border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:outline-none transition-colors"
                />
              ))}
            </div>

            <div className="flex-1" />
          </form>
        ) : (
          <form onSubmit={handleConfirmPin} className="flex-1 px-6 py-8 flex flex-col">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock className="text-indigo-600" size={28} />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Confirm your PIN</h2>
              <p className="text-gray-600">
                Re-enter your 4-digit PIN to confirm.
              </p>
            </div>

            {/* Confirm PIN Input */}
            <div className="flex justify-center gap-4 mb-4">
              {confirmPinValue.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (confirmInputRefs.current[index] = el)}
                  type="password"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleChange(index, e.target.value, true)}
                  onKeyDown={(e) => handleKeyDown(index, e, true)}
                  className={`w-14 h-14 text-center text-2xl font-bold border-2 rounded-xl focus:outline-none transition-colors ${
                    error ? 'border-red-500' : 'border-gray-200 focus:border-indigo-500'
                  }`}
                />
              ))}
            </div>

            {error && (
              <p className="text-center text-sm text-red-500 mb-4">{error}</p>
            )}

            <div className="flex-1" />

            <button
              type="submit"
              disabled={!isConfirmValid || isCreating}
              className={`w-full py-4 rounded-2xl font-bold text-lg transition-colors ${
                isConfirmValid && !isCreating
                  ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              {isCreating ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Creating...
                </span>
              ) : (
                'Continue'
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
